<template>
  <div>
    <v-navigation-drawer
      id="core-navigation-drawer"
      v-model="drawer"
      :dark="dark"
      :expand-on-hover="true"
      :right="$vuetify.rtl"
      v-bind:mini-variant.sync="miniVariant"
      :mini-variant-width="80"
      mobile-break-point="960"
      absolute
      :width="drawerWidth"
      v-bind="$attrs"
      :hide-overlay="false"
    >
      <v-list-item two-line to="/" style="max-height: 75px">
        <v-list-item-content>
          <v-list-item-title
            class="text-uppercase font-weight-regular display-2"
          >
            <span class="logo-mini">
              <img src="/static/logo2.png" width="85" alt="Doc.ECM" />
            </span>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="mb-0" />

      <v-list expand nav>
        <!-- Style cascading bug  -->
        <!-- https://github.com/vuetifyjs/vuetify/pull/8574 -->
        <div />
        <template v-for="(item, i) in userMenu">
          <base-item-group
            v-if="item.children"
            :key="`group-${i}`"
            :item="item"
            @on-child-item-click="onItemClick"
            @on-expanded="setDrawerWidth"
          >
            <!--  -->
          </base-item-group>

          <base-item
            v-else
            :key="`item-${i}`"
            :item="item"
            @on-item-click="onItemClick"
          />
        </template>

        <!-- Style cascading bug  -->
        <!-- https://github.com/vuetifyjs/vuetify/pull/8574 -->
        <div />
      </v-list>
    </v-navigation-drawer>
    <AdvSearchModal :ref="advSearchModalKey" />
    <ParamSearchModal :ref="paramSearchModalKey" />
    <DocumentModal :ref="documentModalKey" />
  </div>
</template>

<script>
// Utilities
import Utils from "@/ecm/common/Utils";
import AdvSearchModal from "@/components/search/advSearch/AdvSearchModal";
import DocumentModal from "@/components/document/documentModal/DocumentModal";
import ParamSearchModal from "@/components/search/advSearch/ParamSearchModal.vue";
import SearchType from "@/ecm/variables/SearchType";

export default {
  name: "AppCoreDrawer",
  components: {
    AdvSearchModal,
    DocumentModal,
    ParamSearchModal,
  },
  props: {},
  data: () => ({
    advSearchModalKey: "advSearchModal",
    paramSearchModalKey: "paramSearchModal",
    documentModalKey: "documentModal",
    drawerWidth: null,
    miniVariant: true,
  }),
  mounted() {
    this.$nextTick(() => {
      this.setDrawerWidth();
    });
  },
  computed: {
    dark() {
      return !this.$store.getters["theme/dark"];
    },
    advSearchModalInstance() {
      return this.$refs[this.advSearchModalKey];
    },
    paramSearchModalInstance() {
      return this.$refs[this.paramSearchModalKey];
    },
    documentModalInstance() {
      return this.$refs[this.documentModalKey];
    },
    drawer: {
      get() {
        return this.$store.getters["theme/drawer"];
      },
      set(val) {
        this.$store.dispatch("theme/setDrawer", val);
      },
    },
    userMenu: {
      get() {
        var menu = [
          {
            icon: "mdi-view-dashboard",
            title: this.$t("Dashboard"),
            to:
              this.$store.getters["account/currentRole"] == 0
                ? "/admin/dashboard"
                : "/dashboard",
          },
        ];

        //User Menu Options
        var userMenu = this.$store.getters["account/userMenu"];
        if (!Utils.object.isEmpty(userMenu)) {
          if (userMenu.CanSearch) {
            menu.push({
              icon: "mdi-magnify-plus",
              title: this.$t("AdvancedSearch"),
              id: "advSearch",
            });

            //User Saved Searches
            var userSavedSearches = this.$store.getters[
              "account/userSavedSearches"
            ];
            if (userSavedSearches != null && userSavedSearches.length > 0) {
              var userSearchesMenu = {
                group: "/userSearches",
                icon: "mdi-folder-search",
                title: this.$t("SavedSearches"),
                children: [],
              };
              for (let index = 0; index < userSavedSearches.length; index++) {
                const opt = userSavedSearches[index];
                userSearchesMenu.children.push({
                  title: opt.FilterName,
                  to: opt.IsParameterizable
                    ? ""
                    : `search/${SearchType.UserSearch}/${opt.Id}`,
                  id: `paramSearch-${opt.Id}`,
                });
              }
              menu.push(userSearchesMenu);
            }
          }

          if (userMenu.CanCreateDocuments) {
            menu.push({
              icon: "mdi-file-plus",
              title: this.$t("NewDocument"),
              id: "newDocument",
            });
          }

          if (userMenu.MenuOptionGroups.length > 0) {
            var admMenu = {
              group: "/administration",
              icon: "mdi-view-comfy",
              title: this.$t("Administration"),
              children: [],
            };
            for (
              let index = 0;
              index < userMenu.MenuOptionGroups.length;
              index++
            ) {
              const optGroup = userMenu.MenuOptionGroups[index];
              var optGroupMenu = {
                group: optGroup.Id,
                icon: optGroup.Icon,
                title: optGroup.Name,
                children: [],
              };
              for (
                let index2 = 0;
                index2 < optGroup.MenuOptions.length;
                index2++
              ) {
                const opt = optGroup.MenuOptions[index2];
                optGroupMenu.children.push({
                  title: opt.Name,
                  to: opt.Url,
                });
              }
              admMenu.children.push(optGroupMenu);
            }
            menu.push(admMenu);
          }
        }
        return menu;
      },
    },
  },
  watch: {
    "$vuetify.breakpoint.mobile": {
      immediate: true,
      handler(val) {
        this.$store.dispatch("theme/setDrawer", !val);
      },
    },
  },
  methods: {
    setDrawerWidth() {
      var defaultWidth = window.innerWidth * 0.2;
      function getTextWidth(text) {
        var canvas =
          getTextWidth.canvas ||
          (getTextWidth.canvas = document.createElement("canvas"));
        var context = canvas.getContext("2d");
        context.font = "14px roboto";
        var metrics = context.measureText(text);
        return metrics.width;
      }
      function getMenuOptionMaxSize(menuOption, level) {
        level += 1;
        var maxWidth = getTextWidth(menuOption.title) + level * 45;
        if (menuOption.children) {
          if (level == 1) {
            maxWidth += 120;
          }
          if (menuOption.expanded) {
            var childrenMax = Math.max(
              ...menuOption.children.map((c) => getMenuOptionMaxSize(c, level))
            );
            if (childrenMax > maxWidth) {
              maxWidth = childrenMax;
            }
          }
        }
        return maxWidth;
      }
      var maxWidth = Math.max(
        ...this.userMenu.map((o) => getMenuOptionMaxSize(o, 0))
      );
      this.drawerWidth = `${
        maxWidth > defaultWidth ? maxWidth : defaultWidth
      }px`;
    },
    onItemClick(itemID) {
      if (itemID) {
        if (itemID.includes("advSearch")) {
          this.advSearchModalInstance.show();
        } else if (itemID.includes("newDocument")) {
          this.documentModalInstance.new();
        } else if (itemID.includes("paramSearch")) {
          var userSearchID = parseInt(
            itemID.replace("paramSearch-", "").trim()
          );
          var userSearch = this.$store.getters[
            "account/userSavedSearches"
          ].find((x) => x.Id == userSearchID);
          if (userSearch && userSearch.IsParameterizable) {
            this.paramSearchModalInstance.edit(userSearch);
          }
        }
      }
    },
  },
};
</script>
<style scoped>
.v-navigation-drawer {
  z-index: 10 !important;
}
</style>